import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { categoryResources, type MagnetResource } from '@/mocks/resources';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const resources: MagnetResource[] = categoryResources[slug || 'movie'] || [];

  const [activeCategory, setActiveCategory] = useState(slug || 'movie');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const handleCategorySwitch = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyMagnet = (hash: string, id: string) => {
    navigator.clipboard.writeText(`magnet:?xt=urn:btih:${hash}`).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const filteredResources = query
    ? resources.filter((r) => r.title.toLowerCase().includes(query.toLowerCase()))
    : resources;

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}磁力资源 - 黑马磁力搜索`}
        description={`黑马磁力为您提供最新最全的${currentCategory.name}磁力链接资源，海量${currentCategory.name}资源一键搜索、高速下载。${currentCategory.description}`}
        keywords={`${currentCategory.name}磁力,${currentCategory.name}下载,${currentCategory.name}资源,${currentCategory.name}磁力链接,黑马磁力`}
        canonicalUrl={`https://heimacili.com/category/${slug}`}
      />
      <SchemaOrg type="WebPage" data={{
        name: `${currentCategory.name} - 黑马磁力`,
        url: `https://heimacili.com/category/${slug}`,
        description: currentCategory.description,
      }} />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: currentCategory.name },
        ]} />

        {/* Category Header */}
        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${currentCategory.icon} text-2xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{currentCategory.name}</h1>
                <p className="text-foreground-500 text-sm mt-1">{currentCategory.description}</p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 flex-wrap mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategorySwitch(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {query && (
              <div className="mb-6 px-4 py-3 rounded-lg bg-accent-50 text-accent-800 text-sm">
                <i className="ri-search-line mr-2"></i>
                搜索 "<strong>{query}</strong>" 的结果，共找到 {filteredResources.length} 个资源
              </div>
            )}

            {/* Resource List */}
            {filteredResources.length > 0 ? (
              <div className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-background-100 text-xs font-medium text-foreground-600">
                  <div className="col-span-5">资源名称</div>
                  <div className="col-span-2">大小</div>
                  <div className="col-span-1">日期</div>
                  <div className="col-span-2 text-center">种子/下载</div>
                  <div className="col-span-2 text-right">操作</div>
                </div>
                {filteredResources.map((res, idx) => (
                  <div
                    key={res.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-background-200 last:border-b-0 items-center hover:bg-background-50/50 transition-colors"
                  >
                    <div className="col-span-5 flex items-center gap-3 min-w-0">
                      <span className="text-xs font-bold text-primary-500 w-5 flex-shrink-0 hidden md:inline">{idx + 1}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground-800 truncate">{res.title}</div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {res.tags.map((tag) => (
                            <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-accent-100 text-accent-700 font-medium">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 text-sm text-foreground-600 hidden md:block">{res.size}</div>
                    <div className="col-span-1 text-sm text-foreground-500 hidden md:block">{res.date}</div>
                    <div className="col-span-2 text-center hidden md:block">
                      <span className="text-sm text-accent-600 font-medium">{res.seeds.toLocaleString()}</span>
                      <span className="text-xs text-foreground-400"> / {res.leeches.toLocaleString()}</span>
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end gap-2">
                      <span className="text-xs text-foreground-500 md:hidden">{res.size} · {res.date}</span>
                      <button
                        onClick={() => handleCopyMagnet(res.magnetHash, res.id)}
                        className="px-3 py-1.5 rounded-md bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {copiedId === res.id ? (
                          <><i className="ri-check-line mr-1"></i>已复制</>
                        ) : (
                          <><i className="ri-link mr-1"></i>复制链接</>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无资源</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}